import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "components/buttons";
import { Dropdown } from "components/dropdown";
import { Text } from "components/text";
import { TreeViewComponent } from "components/treeview";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";
import { IConnection, IProjectProps } from "data/types";
import { SampleBrowser } from "components/sampleBrowser";
import { LabelMap } from "data/Sample";
import { Star, StarOutline } from "@mui/icons-material";
import { useFavorites } from "hooks/useFavorites";
import { useProject } from "hooks/useProject";

export interface IFormProps {
    title?: string;
    key: string;
}

export function Project() {
    const [error, setError] = useState<string>();
    const [projects, setProjects] = useState<IProjectProps[]>([]);
    const location = useParams<{ projectId: string }>();
    const [projectId, setProjectId] = useState<string>(location.projectId,);
    const [forms, setForms] = useState<IFormProps[]>([]);
    const [samples, setSamples] = useState<LabelMap>();
    const [connections, setConnections] = useState<IConnection[]>([]);
    const [formsLoaded, setFormsLoaded] = useState<boolean>(false);
    const cartaObjectStorage = useCartaObjectStorage();
    const projectCache = useProject();
    const { isFavorite, toggleFavorite } = useFavorites();

    useEffect(() => {
        (async () => {
            try {
                if (!cartaObjectStorage) {
                    return;
                }

                const projectList = projectCache.getProjects();
                if (!projectList) {
                    // Not loaded yet; this effect re-runs once ProjectCacheProvider's
                    // startup fetch populates it.
                    return;
                }

                setFormsLoaded(false);
                setProjects(projectList);

                if (projectList.length === 1) setProjectId(projectList[0].projectId);
            } catch (error) {
                if (error instanceof Error) setError(error.message);
                else setError("An unknown error occurred.");
            }
        })();
    }, [cartaObjectStorage, projectCache]);

    useEffect(() => {
        (async () => {
            try {

                if (!cartaObjectStorage) {
                    return;
                }
                if (!projectId) return;

                const cached = projectCache.get(projectId);
                if (cached) {
                    setForms(cached.forms);
                    setConnections(cached.connections);
                    setSamples(cached.samples);
                    setFormsLoaded(true);
                    return;
                }

                // A different project is loading; drop whatever was cached for the last one.
                projectCache.clear();

                const forms = await cartaObjectStorage.listSchemas({ projectId });
                setForms(forms);
                setFormsLoaded(true);

                const connectionsResult = await cartaObjectStorage.listConnections({ projectId });
                setConnections(connectionsResult);

                const samples = await cartaObjectStorage.getSamples({
                    projectId: projectId,
                    connectionId: connectionsResult[0].connectionId,
                });
                setSamples(samples);

                projectCache.set(projectId, { forms, connections: connectionsResult, samples });
            } catch (error) {
                if (error instanceof Error) setError(error.message);
                else setError("An unknown error occurred.");
            }
        })();
    }, [projectId, cartaObjectStorage, projectCache]);

    const handleExport = () => { };

    // Sort forms: favorites first, then alphabetically
    const sortedForms = [...forms].sort((a, b) => {
        const aFav = isFavorite(a.key);
        const bFav = isFavorite(b.key);
        if (aFav && !bFav) return -1;
        if (!aFav && bFav) return 1;
        return 0;
    });

    const enabledButtonStyle = {
        color: "white",
        backgroundColor: "#6c757d",
        borderColor: "#6c757d",
        padding: ".375rem .75rem",
    };

    if (!projectId) {
        return (<>Invalid project</>)
    }


    if (!formsLoaded) {
        return (error ? (<Text color="error" size="small" padding="center"> {error} </Text>) : (<Text size="medium">Loading...</Text>))
    }

    return (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", flex: 1 }}>
            <div style={{ flex: 1, flexBasis: "50%" }}>
                <Text size="medium">Select Existing</Text>
                {connections.map((item: IConnection) => (
                    <TreeViewComponent
                        key={item.connectionId}
                        connectionId={item.connectionId}
                        node={item.node}
                        projectId={projectId}
                        forms={forms}
                    />
                ))}
                <Button onClick={handleExport} style={enabledButtonStyle} disabled>
                    Export All
                </Button>
                <Dropdown side="bottom-right">
                    <Dropdown.Toggler caret>
                        <Text size="medium">Start New</Text>
                    </Dropdown.Toggler>
                    <Dropdown.Area>
                        {sortedForms.map((item: IFormProps) => {
                            const favorited = isFavorite(item.key);
                            const title = (item.title && item.title) || item.key.substring(0, item.key.lastIndexOf("."));
                            const project = projects.find(p => p.projectId === projectId);
                            return (
                                <Dropdown.Item
                                    key={item.key}
                                    href={`/${projectId}/form?key=${item.key}`}
                                    icon={favorited ? <Star fontSize="small" /> : <StarOutline fontSize="small" />}
                                    iconAlwaysVisible={favorited}
                                    onIconClick={(e) => {
                                        e.preventDefault();
                                        toggleFavorite({
                                            key: item.key,
                                            title,
                                            projectId: projectId!,
                                            projectName: project?.name || ''
                                        });
                                    }}
                                >
                                    {title}
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown.Area>
                </Dropdown>
                <Text color="error" size="small" padding="center">
                    {error}
                </Text>
            </div>
            {samples && connections ? (
                <SampleBrowser
                    samples={samples}
                    connection={{ projectId, connectionId: connections[0].connectionId }}
                    style={{ flex: 1, flexBasis: "50%" }}></SampleBrowser>
            ) : (
                <></>
            )}
        </div>
    )
}

